import {
  Attributes,
  AuthorizationEvaluator,
  AuthorizationMap,
  EvaluateRequestBuilder,
  EvaluateResponse,
  buildUserContext,
  isEvaluateErrorResponse,
} from "@avela/avela-authorization-sdk";
import React from "react";
import {
  AuthorizationScope,
  usePermissionDispatcher,
} from "src/components/Providers/AuthorizationProvider";
import useAccessToken from "src/hooks/useAccessToken";
import { useOrganization } from "src/hooks/useOrganization";
import { useTeam } from "src/hooks/useTeam";
import useUser from "src/hooks/useUser";
import { Status } from "src/types/authData";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";

export class AuthorizationError extends Error {
  constructor(message: string, readonly cause?: any) {
    super(message);
    this.cause = cause;
  }
}

export const useAuthorizationApi = (
  scope: AuthorizationScope,
  index?: string
) => {
  const baseUrl = import.meta.env.VITE_AUTHORIZATION_URL;
  const user = useUser();
  const accessToken = useAccessToken();
  const organizationRD = useOrganization();
  const teamRD = useTeam();
  const [bearerToken, setBearerToken] = React.useState<string>();
  const [organizationId, setOrganizationId] = React.useState<string>();
  const [teamId, setTeamId] = React.useState<string | null>();

  const [isReadyToEvaluate, setIsReadyToEvaluate] =
    React.useState<boolean>(false);

  const evaluator = React.useMemo(
    () => new AuthorizationEvaluator(`${baseUrl!}/evaluate`),
    [baseUrl]
  );

  const dispatchPermission = usePermissionDispatcher(scope, index);

  React.useEffect(() => {
    if (accessToken.status === Status.OK)
      setBearerToken(`Bearer ${accessToken.data}`);
  }, [accessToken]);

  React.useEffect(() => {
    if (organizationRD.kind === RD.RemoteDataKind.Success)
      setOrganizationId(organizationRD.data.id);
  }, [organizationRD]);

  React.useEffect(() => {
    if (teamRD.kind === RD.RemoteDataKind.Success) setTeamId(teamRD.data);
  }, [teamRD]);

  React.useEffect(() => {
    if (!baseUrl || !bearerToken) return;
    if (user.status !== "ok") return;
    // Avela Admin users (role === "admin") don't need an organization or team - they use avela-admin.cedar policy
    const isAvelaAdmin = user.data.role === HasuraRole.ADMIN;
    if (organizationId === undefined && !isAvelaAdmin) return;
    // Avela Admins can proceed without team data (team query is skipped when no organization)
    if (!teamRD.hasData() && !isAvelaAdmin) return;
    setIsReadyToEvaluate(true);
  }, [bearerToken, baseUrl, user, organizationId, teamRD]);

  const evaluateWithPrincipalOverride = React.useCallback(
    async (
      overridePrincipal: Attributes,
      ...builders: EvaluateRequestBuilder[]
    ): Promise<RD.RemoteData<Error, AuthorizationMap>> => {
      dispatchPermission(RD.loading());
      // Allow Avela Admin users to proceed without organizationId (they use avela-admin.cedar policy)
      const isAvelaAdmin =
        user.status === "ok" && user.data.role === HasuraRole.ADMIN;
      if (
        !isReadyToEvaluate ||
        user.status !== "ok" ||
        (organizationId === undefined && !isAvelaAdmin)
      ) {
        const err = RD.failure<Error, AuthorizationMap>(
          new AuthorizationError("Not ready to evaluate permissions")
        );
        dispatchPermission(err);
        return err;
      }

      const userContext = buildUserContext({
        userId: user.data.id,
        attributes: {
          legacyPermissions: user.data.permissions,
          ...overridePrincipal,
        },
        roleId: user.data.role,
      });

      const topLevelPolicies =
        user.data.role === HasuraRole.ADMIN
          ? ["avela-admin.cedar"]
          : [`organizations/${organizationId}.cedar`];

      const teamPolicyKeys = teamId
        ? [`organizations/${organizationId}/teams/${teamId}.cedar`]
        : [];

      let response: EvaluateResponse;
      try {
        response = await evaluator.evaluate({
          builders,
          token: bearerToken!,
          userContext,
          policyKeys: [...topLevelPolicies, ...teamPolicyKeys],
          salt: user.data.tokenIssuedAt,
        });
      } catch (error) {
        const err = RD.failure<Error, AuthorizationMap>(
          new AuthorizationError("Failed evaluate permissions", error)
        );
        dispatchPermission(err);
        return err;
      }

      if (isEvaluateErrorResponse(response)) {
        const err = RD.failure<Error, AuthorizationMap>(
          new AuthorizationError(
            "Failed evaluate permissions",
            response.message
          )
        );
        dispatchPermission(err);
        return err;
      }

      const remoteDataResponse: RD.RemoteData<Error, AuthorizationMap> =
        RD.success(response);

      dispatchPermission(remoteDataResponse);
      return remoteDataResponse;
    },
    [
      user,
      teamId,
      evaluator,
      bearerToken,
      organizationId,
      isReadyToEvaluate,
      dispatchPermission,
    ]
  );

  const evaluate = React.useCallback(
    async (
      ...builders: EvaluateRequestBuilder[]
    ): Promise<RD.RemoteData<Error, AuthorizationMap>> => {
      return evaluateWithPrincipalOverride({}, ...builders);
    },
    [evaluateWithPrincipalOverride]
  );

  return {
    evaluate,
    evaluateWithPrincipalOverride,
    isReadyToEvaluate,
  };
};
